import { useMemo } from "react";
import { SelectInput, TriangleAlert, type SelectOption } from "@repo/ui";
import { useMultipleWorkflowStatuses } from "../../../hooks/apis/use-statuses";

type Props = {
  workflowIds: number[];
  selectedStatusCode: number | null;
  onStatusChange: (statusCode: number) => void;
  actionStatusCodes?: Set<number>;
};

export const ChangeStatusDropdown = ({
  workflowIds,
  selectedStatusCode,
  onStatusChange,
  actionStatusCodes,
}: Props) => {
  const hasWorkflows = workflowIds.length > 0;
  const latestWorkflowId = hasWorkflows ? Math.max(...workflowIds) : null;
  const { statuses, isLoading } = useMultipleWorkflowStatuses(
    latestWorkflowId !== null ? [latestWorkflowId] : [],
    hasWorkflows
  );

  const statusOptions: SelectOption<number>[] = useMemo(() => {
    if (!statuses) return [];
    return statuses.map((status) => ({
      value: status.code,
      label: `${status.code} - ${status.name}`,
      disabled: actionStatusCodes?.has(status.code),
      suffix: actionStatusCodes?.has(status.code) ? (
        <span
          title="This status has actions attached"
          role="img"
          aria-label="This status has actions attached"
        >
          <TriangleAlert className="h-4 w-4 text-yellow-500" />
        </span>
      ) : undefined,
    }));
  }, [statuses, actionStatusCodes]);

  const sortedStatusOptions = useMemo(
    () => [...statusOptions].sort((a, b) => a.value - b.value),
    [statusOptions]
  );

  if (isLoading) {
    return (
      <div className="flex h-10 w-full items-center justify-center rounded-md border border-gray-300 bg-white">
        <span className="text-sm text-gray-500">Loading statuses...</span>
      </div>
    );
  }

  if (!statusOptions || statusOptions.length === 0) {
    return (
      <div className="flex h-10 w-full items-center justify-center rounded-md border border-gray-300 bg-white">
        <span className="text-sm text-gray-500">No statuses available</span>
      </div>
    );
  }

  return (
    <SelectInput
      options={sortedStatusOptions}
      value={selectedStatusCode ?? undefined}
      onChange={(statusCode) => onStatusChange(statusCode as number)}
      placeholder="Select status"
      enableScrollToSelected={true}
    />
  );
};
